'use strict';

/**
 * @module client
 * Handles all client-side interactions for the Career Job Form including validation,
 * sanitization, file upload, and form submission.
 */

var toastMessageHelper = require('../../utils/toastMessageHelper');
var $form = $('#career-job-form');

/**
 * @function showError
 * @description Displays the given error message and applies invalid styling.
 * @param {jQuery} $input - The input element.
 * @param {jQuery} $errorElement - The corresponding error message element.
 */
function showError($input, $errorElement) {
    $input.addClass('is-invalid');
    $errorElement.removeClass('d-none');
}

/**
 * @function hideError
 * @description Hides the given error message and removes invalid styling.
 * @param {jQuery} $input - The input element.
 * @param {jQuery} $errorElement - The corresponding error message element.
 */
function hideError($input, $errorElement) {
    $input.removeClass('is-invalid');
    $errorElement.addClass('d-none');
}

/**
 * Marks the "Customer Title" dropdown in the career job form as invalid by
 * adding the `is-invalid` CSS class to the dropdown container element.
 *
 * @function addInvalidToCustomerTitle
 * @returns {void}
 */
function addInvalidToCustomerTitle() {
    $('.career-job-form-customer-title .custom-drop-down-section').addClass('is-invalid');
}

/**
 * Removes the validation error state from the customer title dropdown in the career job form.
 *
 * Finds the `.custom-drop-down-section` element within `.career-job-form-customer-title`
 * and removes the `is-invalid` CSS class.
 *
 * @function removeInvalidFromCustomerTitle
 * @returns {void}
 */
function removeInvalidFromCustomerTitle() {
    $('.career-job-form-customer-title .custom-drop-down-section').removeClass('is-invalid');
}

/**
 * Marks the mobile dial-code dropdown section in the career job form as invalid.
 *
 * Adds the `is-invalid` CSS class to `.career-job-form-mobile-dia-code .custom-drop-down-section`
 * using jQuery, typically to reflect a validation error state in the UI.
 *
 * @function addInvalidToMobileDiaCode
 * @returns {void}
 */
function addInvalidToMobileDiaCode() {
    $('.career-job-form-mobile-dia-code .custom-drop-down-section').addClass('is-invalid');
}

/**
 * Removes the "is-invalid" validation class from the mobile dial code custom dropdown
 * within the career job form UI.
 *
 * This is typically used to clear an error state after the user corrects or reselects
 * the dial code.
 *
 * @function removeInvalidFromMobileDiaCode
 * @returns {void}
 */
function removeInvalidFromMobileDiaCode() {
    $('.career-job-form-mobile-dia-code .custom-drop-down-section').removeClass('is-invalid');
}

/**
 * @function sanitizeName
 * @description Removes digits, special characters, trims extra spaces, and ensures proper formatting for names.
 * @param {string} value - The input name value.
 * @returns {string} Sanitized name.
 */
function sanitizeName(value) {
    return value.replace(/[^a-zA-Z\s]/g, '').replace(/^\s+/, '').replace(/\s{2,}/g, ' ');
}

/**
 * Retrieves the currently selected country dial code from the career job form UI.
 *
 * Reads the trimmed text content of the `.selected-value` element within
 * `.career-job-form-mobile-dia-code`.
 *
 * @returns {string} The selected country code/dial code text, or an empty string if none is selected.
 */
function getSelectedCountryCode() {
    return $('.career-job-form-mobile-dia-code .selected-value').text().trim();
}

/**
 * @function sanitizePhone
 * @description Allows only numbers, limits to 10 digits, and removes invalid starting digits.
 * @param {string} value - The phone input value.
 * @returns {string} Sanitized phone number.
 */
function sanitizePhone(value) {
    let maxLength = $('#mobile-number').attr('maxlength');
    let sanitized = value.replace(/[^0-9]/g, '').substring(0, maxLength);
    var countryCode = getSelectedCountryCode();

    // If +91 → block starting digits 0–6
    if (countryCode === '+91' && /^[0-6]/.test(sanitized)) {
        sanitized = sanitized.replace(/^[0-6]+/, '');
    }

    return sanitized;
}

/**
 * @function sanitizeEmail
 * @description Removes invalid characters and returns only valid email characters.
 * @param {string} value - The email input value.
 * @returns {string} Sanitized email.
 */
function sanitizeEmail(value) {
    return value.replace(/[^a-zA-Z0-9@._%+-]/g, '');
}

/**
 * @function validateEmail
 * @description Validates email format; must start with a letter and follow standard pattern.
 * @param {string} email - The email to validate.
 * @returns {boolean} True if valid, otherwise false.
 */
function validateEmail(email) {
    const emailRegex = /^(?=[A-Za-z0-9._%+-]*[A-Za-z])[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email);
}

/**
 * @function uploadFile
 * @description Handles resume file upload via AJAX.
 * @param {string} url - The endpoint URL.
 * @param {File} file - The file to upload.
 * @param {Function} callback - Success callback.
 */
function uploadFile(url, file, callback) {
    var formData = new FormData();
    formData.append('resumeFile', file);

    $.ajax({
        url: url,
        type: 'POST',
        data: formData,
        processData: false,
        contentType: false,
        success: callback
    });
}

/**
 * Resets the resume upload section to its initial state.
 *
 * - Clears the file input value.
 * - Shows the upload area.
 * - Hides the uploaded file preview.
 * - Hides any validation error messages (invalid format or size).
 *
 * @function resetResumeUploadSection
 */
function resetResumeUploadSection() {
    var $uploadSection = $('.ckc-resume-upload');
    $uploadSection.find('#resume-file').val('');
    $uploadSection.find('.ckc-upload-area').removeClass('d-none');
    $uploadSection.find('.ckc-uploaded-file').addClass('d-none');
    $uploadSection.find('.invalid-file-format, .invalid-file-size').addClass('d-none').removeClass('d-block');
}

/**
 * Resets the "customer title" dropdown selection in the career job form UI.
 *
 * Locates the `.career-job-form-customer-title` section, finds the custom dropdown
 * options (`.custom-option-value`), then selects either:
 * - the option matching `customerTitle` (by `data-title` or trimmed text), or
 * - the first option if `customerTitle` is empty/undefined or no match is found.
 *
 * After determining the target option, it:
 * - toggles the `selected` class on options,
 * - updates the visible selected label (`.selected-value`),
 * - updates the hidden input `input[name="customerTitle"]` with the option's `data-code`.
 *
 * The function safely no-ops (returns early) if required DOM elements are missing.
 *
 * @function resetCustomerTitle
 * @returns {void}
 */
function resetCustomerTitle() {
    var careerFormCustomerTitle = ''; // Default to empty string
    var $nameSection = $('.career-job-form-customer-title');
    if (!$nameSection.length) return;

    var $dropdown = $nameSection.find('.custom-drop-down-section');
    if (!$dropdown.length) return;

    var $options = $dropdown.find('.custom-option-value');
    if (!$options.length) return;

    var $display = $dropdown.find('.selected-value').first();
    var $hiddenInput = $nameSection.find('input[name="customerTitle"]');

    var $targetOption;

    // If customerTitle is passed → select matching option
    if (careerFormCustomerTitle) {
        $targetOption = $options.filter(function () {
            var title = $(this).attr('data-title') || $(this).text().trim();
            return title === careerFormCustomerTitle;
        }).first();
    }

    // If NOT passed or not found → select FIRST <li>
    if (!$targetOption || !$targetOption.length) {
        $targetOption = $options.first();
    }

    var nextTitle = $targetOption.attr('data-title') || $targetOption.text().trim();
    var nextCode = $targetOption.attr('data-code');

    // Reset selection
    $options.removeClass('selected');
    $targetOption.addClass('selected');

    // Update visible text
    if ($display.length) {
        $display.text(nextTitle);
    }

    // Update hidden field
    if ($hiddenInput.length && nextCode) {
        $hiddenInput.val(nextCode);
    }
}

/**
 * Resets the country dial code UI in the career job form to a default value.
 *
 * Determines the default selection from the `.career-job-form-mobile-dia-code` container:
 * - If a `countryCode` value is available, selects the first option matching
 *   `.custom-option-value[data-id="<countryCode>"]`.
 * - Otherwise, selects the first `.custom-option-value` in the list.
 *
 * When a default option is found, it:
 * - Updates `.selected-value` and `.country-selected-value` text to the selected code and marks them as active.
 * - Clears existing `selected` states from `.custom-option-value` and `.country-option-item`, then marks the default option as selected.
 * - Marks the container as selected and hides dropdown elements (`.custom-option-values`, `.country-dropdown-options`).
 *
 * @function resetCountryCodeToDefault
 * @returns {void}
 */
function resetCountryCodeToDefault() {
    var careerFormCountryCode = ''; // Default to empty string
    var $container = $('.career-job-form-mobile-dia-code');
    var $defaultOption;

    // If countryCode is passed → use it
    if (careerFormCountryCode) {
        $defaultOption = $container
            .find('.custom-option-value[data-id="' + careerFormCountryCode + '"]')
            .first();
    } else { // Else → pick FIRST option from the list
        $defaultOption = $container.find('.custom-option-value').first();
    }

    if ($defaultOption.length) {
        var defaultCode = $defaultOption.data('id');

        // Update display
        $container.find('.selected-value, .country-selected-value')
            .text(defaultCode)
            .addClass('active');

        // Reset selected states
        $container.find('.custom-option-value, .country-option-item')
            .removeClass('selected');

        $defaultOption.addClass('selected');
        $container.addClass('selected');

        // Hide dropdown
        $container.find('.custom-option-values, .country-dropdown-options').hide();
    }
}

$(document).ready(function () {
    var $customerTitle = $('.career-job-form-customer-title');
    var $mobileDiaCode = $('.career-job-form-mobile-dia-code');

    var customerTitleURL = $customerTitle.data('url');
    var mobileDiaCodeURL = $mobileDiaCode.data('url');

    // Load Customer Title
    if ($form.length && customerTitleURL) {
        $.ajax({
            url: customerTitleURL,
            type: 'GET',
            success: function (data) {
                $customerTitle.html(data);
            }
        });
    }

    // Load Mobile Dial Code
    if ($form.length && mobileDiaCodeURL) {
        $.ajax({
            url: mobileDiaCodeURL,
            type: 'GET',
            success: function (data) {
                $mobileDiaCode.html(data);
            }
        });
    }

    // Clear phone input when country code changes
    $('body').on('click', '.career-job-form-mobile-dia-code .custom-option-value.custom-option-login', function () {
        $('#mobile-number').val(''); // clear phone number
        $('.phone-input-field').removeClass('is-invalid'); // remove invalid state
        $('.career-job-form-input.input-mobile').removeClass('is-invalid');
        $('.invalid-feedback-phone').find('.missing-phone, .invalid-phone').addClass('d-none');

        var phoneLength = $(this).data('phonelength');
        $('#mobile-number').attr('maxlength', phoneLength);
    });

    // Input Field Blur
    $form.find('.career-job-form-input').on('blur', function () {
        var $input = $(this);
        var inputName = $input.attr('name');
        var $missingError = $input.closest('.form-group').find(`.missing-${inputName}`);
        var $invalidError = $input.closest('.form-group').find(`.invalid-${inputName}`);

        // Common Empty Field Check
        if ($input.val().trim() === '') {
            showError($input, $missingError);
            $invalidError.addClass('d-none');

            if ($input.hasClass('first-name-input')) {
                addInvalidToCustomerTitle();
            }
        } else {
            hideError($input, $missingError);
            if ($input.hasClass('first-name-input')) {
                $('.career-job-form-customer-title .custom-drop-down-section').removeClass('is-invalid  ');
            }
        }

        // Phone Field Validation
        if ($input.hasClass('input-mobile')) {
            const phoneVal = $input.val().trim();
            let maxLength = $('#mobile-number').attr('maxlength');

            if (phoneVal.trim() === '') {
                showError($input, $missingError);
                addInvalidToMobileDiaCode();
                return;
            }

            // If less than 10 digits → invalid
            if (phoneVal.length < maxLength) {
                showError($input, $invalidError);
                addInvalidToMobileDiaCode();
            }
        }

        // Email Field Validation
        if ($input.hasClass('input-email')) {
            const emailVal = sanitizeEmail($input.val());
            $input.val(emailVal);

            $invalidError.addClass('d-none').removeClass('d-block');
            $missingError.addClass('d-none');
            $input.removeClass('is-invalid');

            if (emailVal.trim() === '') {
                showError($input, $missingError);
                return;
            }

            if (!validateEmail(emailVal)) {
                $invalidError.removeClass('d-none').addClass('d-block');
                $input.addClass('is-invalid');
            }
        }
    });

    // Input Field Input Event
    $form.find('.career-job-form-input').on('input', function () {
        var $input = $(this);
        var inputName = $input.attr('name');
        var $missingError = $input.closest('.form-group').find(`.missing-${inputName}`);
        var $invalidError = $input.closest('.form-group').find(`.invalid-${inputName}`);

        if ($input.hasClass('first-name-input') || $input.hasClass('last-name-input')) {
            $input.val(sanitizeName($input.val()));
        }

        if ($input.hasClass('input-mobile')) {
            $input.val(sanitizePhone($input.val()));
        }

        if ($input.hasClass('input-email')) {
            $input.val(sanitizeEmail($input.val()));
            $('.invalid-email').removeClass('d-block');
        }

        $missingError.addClass('d-none');

        if ($input.val().trim() !== '') {
            hideError($input, $invalidError);

            if ($input.hasClass('first-name-input')) {
                removeInvalidFromCustomerTitle();
            }

            if ($input.hasClass('input-mobile')) {
                removeInvalidFromMobileDiaCode();
            }
        } else {
            showError($input, $invalidError);

            if ($input.hasClass('first-name-input')) {
                addInvalidToCustomerTitle();
            }

            if ($input.hasClass('input-mobile')) {
                addInvalidToMobileDiaCode();
            }
        }
    });

    // Resume Upload Section
    $('.ckc-resume-upload').each(function () {
        var $this = $(this);

        var fileInput = $this.find('.ckc-resume-input');
        var uploadArea = $this.find('.ckc-upload-area');
        var uploadedFile = $this.find('.ckc-uploaded-file');
        var fileNameText = $this.find('.ckc-uploaded-file-name');
        var removeBtn = $this.find('.ckc-remove-file-btn');
        var invalidFormat = $this.find('.invalid-file-format');
        var invalidSize = $this.find('.invalid-file-size');

        var allowedTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/zip'
        ];

        var MAX_SIZE = 2 * 1024 * 1024; // 2MB

        fileInput.on('change', function (e) {
            invalidFormat.addClass('d-none').removeClass('d-block');
            invalidSize.addClass('d-none').removeClass('d-block');

            var file = e.target.files[0];
            if (!file) return;

            if (!allowedTypes.includes(file.type)) {
                invalidFormat.removeClass('d-none').addClass('d-block');
                fileInput.val('');
                return;
            }

            if (file.size > MAX_SIZE) {
                invalidSize.removeClass('d-none').addClass('d-block');
                fileInput.val('');
                return;
            }

            fileNameText.text(file.name + ' Uploaded');
            uploadArea.addClass('d-none');
            uploadedFile.removeClass('d-none');
        });

        removeBtn.on('click', function () {
            fileInput.val('');
            uploadArea.removeClass('d-none');
            uploadedFile.addClass('d-none');
            invalidFormat.addClass('d-none').removeClass('d-block');
            invalidSize.addClass('d-none').removeClass('d-block');
        });
    });
});

$('#resume-upload-form').on('submit', function (e) {
    e.preventDefault();
    var $this = $(this);
    var url = $this.data('url');
    var fileInput = $this.find('#resume-file')[0];
    var file = fileInput.files[0];

    if (!file) {
        $('.invalid-file-format').removeClass('d-none').addClass('d-block');
        $('.invalid-file-size').addClass('d-none').removeClass('d-block');
    } else {
        uploadFile(url, file, function (response) {
            toastMessageHelper.showNotification(response.message);
            resetResumeUploadSection();
        });
    }
});

// Submit Handler
module.exports = {
    submitJobForm: function () {
        $('#career-job-form').on('submit', function (e) {
            e.preventDefault();

            var $this = $(this);
            var url = $this.data('url');
            var jobId = $this.data('jobid');
            var customerTitle = $('.first-name-container .selected-value').text().trim();
            var fname = $('#first-name').val().trim();
            var lname = $('#last-name').val().trim();
            var mobileDiaCode = $('.mobile-number-container #select-country-code').text().trim();
            var phone = $('#mobile-number').val();
            var email = $('#email').val();
            var fileInput = $('#resume-file')[0];
            var file = fileInput.files[0];

            var hasError = false;

            if (!fname) {
                showError($('.first-name-input'), $('.missing-firstName'));
                addInvalidToCustomerTitle();
                hasError = true;
            }

            if (!lname) {
                showError($('.last-name-input'), $('.missing-lastName'));
                hasError = true;
            }

            if (!phone) {
                showError($('.input-mobile'), $('.missing-phone'));
                addInvalidToMobileDiaCode();
                hasError = true;
            }

            if (!email) {
                showError($('.input-email'), $('.missing-email'));
                hasError = true;
            }

            if (!file) {
                $('.invalid-file-format').removeClass('d-none').addClass('d-block');
                $('.invalid-file-size').addClass('d-none').removeClass('d-block');
                hasError = true;
            }

            if (hasError) return;

            var formData = new FormData();
            formData.append('customerTitle', customerTitle);
            formData.append('fname', fname);
            formData.append('lname', lname);
            formData.append('mobileDiaCode', mobileDiaCode);
            formData.append('phone', phone);
            formData.append('email', email);
            formData.append('jobId', jobId);
            formData.append('resumeFile', file);

            $.ajax({
                url: url,
                type: 'post',
                dataType: 'json',
                data: formData,
                processData: false,
                contentType: false,
                success: function (data) {
                    if (data.success) {
                        toastMessageHelper.showNotification(data.message);
                        $('#career-job-form')[0].reset();
                        resetResumeUploadSection();
                        resetCustomerTitle();
                        resetCountryCodeToDefault();
                    }
                }
            });
        });
    }
};
